import { Issue, IssueLevel, RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";
import { DateTime } from "luxon";

import { formatContainerDate, isOutsideEpisodicWindow } from "./containerDisplay";

/**
 * The federal central-accumulation window, in days, by generator status.
 *
 * VSQG is absent on purpose rather than mapped to a number: a very small
 * quantity generator has no federal time limit at all, and inventing one for
 * the preview would show a countdown the saved container will not have.
 */
const CENTRAL_ACCUMULATION_DAYS: Partial<Record<RcraInfoFederalWasteGeneratorStatus, number>> = {
  [RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator]: 90,
  [RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator]: 180,
};

/** An SQG shipping over 200 miles to its designated facility gets 270 days. */
const SQG_OVER_200_MILES_DAYS = 270;

const GENERATOR_STATUS_ABBREVIATION: Partial<Record<RcraInfoFederalWasteGeneratorStatus, string>> =
  {
    [RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator]: "LQG",
    [RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator]: "SQG",
    [RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator]: "VSQG",
  };

export type ClockProjection = {
  /** Length of the accumulation window. */
  totalDays: number;
  /** Whole days between today and the deadline; negative once it has passed. */
  daysRemaining: number;
  deadline: DateTime;
  /** "180 days — SQG, central accumulation". */
  basis: string;
};

/**
 * How long the container being drafted will have, so the form can show its
 * clock before the container exists.
 *
 * Deliberately a PREVIEW, not a second implementation of the clock: it reads
 * only the federal central-accumulation rule off the two inputs the form
 * already shows the user (the facility's generator status and the date the
 * container moves to central). The saved container's clock is computed by the
 * accumulation engine, which also applies state rules and satellite move
 * windows — so a state with a shorter window will show a nearer deadline on
 * the container than this projected here. Anything this can't state plainly
 * from those two inputs returns null and the rail says nothing rather than
 * guessing.
 */
export function projectCentralAccumulationClock({
  generatorStatus,
  movedToCentralDate,
  sqgOver200Miles,
  now = DateTime.local(),
}: {
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
  /** ISO date; the clock does not start until the container reaches central. */
  movedToCentralDate: string | null;
  sqgOver200Miles: boolean;
  now?: DateTime;
}): ClockProjection | null {
  if (!generatorStatus || !movedToCentralDate) {
    return null;
  }
  const baseDays = CENTRAL_ACCUMULATION_DAYS[generatorStatus];
  if (baseDays == null) {
    return null;
  }
  const isSqg = generatorStatus === RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator;
  const totalDays = isSqg && sqgOver200Miles ? SQG_OVER_200_MILES_DAYS : baseDays;

  const start = DateTime.fromISO(movedToCentralDate);
  if (!start.isValid) {
    return null;
  }
  const deadline = start.plus({ days: totalDays });
  // Day-granular, on calendar days rather than elapsed hours, so the number
  // matches the engine's own whole-day countdown.
  const daysRemaining = Math.round(deadline.startOf("day").diff(now.startOf("day"), "days").days);
  const abbreviation = GENERATOR_STATUS_ABBREVIATION[generatorStatus] ?? generatorStatus;

  return {
    totalDays,
    daysRemaining,
    deadline,
    basis: `${totalDays} days — ${abbreviation}, central accumulation`,
  };
}

/** The event a container is being created under, when it is being created
 * from one. Its clock replaces the central-accumulation projection: the two
 * compete for the same obligation and the engine publishes only the binding
 * one, so showing both would promise a container two deadlines. */
export type PreviewEpisodicEvent = {
  reason: string;
  startDate: string;
  accumulationDeadline: string;
};

/**
 * The bounds an episodic event puts on the two dates that decide when this
 * container's waste was generated and when its clock starts. Both must land
 * inside the event's window, or the container can't join it: the attach guard
 * refuses waste dated outside, and the initial amount is written as a fill on
 * the accumulation start date.
 *
 * `disableFuture` still applies on top for the accumulation start — a window
 * running into the future doesn't make tomorrow a date waste can arrive on.
 */
export function episodicDateBounds(episodicEvent: PreviewEpisodicEvent | undefined): {
  minDate?: DateTime;
  maxDate?: DateTime;
} {
  if (!episodicEvent) {
    return {};
  }
  return {
    minDate: DateTime.fromISO(episodicEvent.startDate),
    maxDate: DateTime.fromISO(episodicEvent.accumulationDeadline),
  };
}

/**
 * The window a container joining an episodic event will clock to.
 *
 * Anchored to the EVENT's start, not the container's — subpart L gives the
 * event's waste 60 days from the day the event began, however long the
 * container has existed. Nothing the form collects can move it, so unlike the
 * central projection this needs no field to be filled in and never returns
 * null for want of one; the deadline comes off the event itself rather than
 * being recomputed here, so the preview and the saved container's clock cannot
 * disagree about the date.
 */
export function projectEpisodicClock({
  startDate,
  accumulationDeadline,
  now = DateTime.local(),
}: {
  /** ISO date the event began. */
  startDate: string;
  /** ISO date the event's waste must be gone by, as the server computed it. */
  accumulationDeadline: string;
  now?: DateTime;
}): ClockProjection | null {
  const start = DateTime.fromISO(startDate);
  const deadline = DateTime.fromISO(accumulationDeadline);
  if (!start.isValid || !deadline.isValid) {
    return null;
  }
  const totalDays = Math.round(deadline.startOf("day").diff(start.startOf("day"), "days").days);
  if (totalDays <= 0) {
    return null;
  }
  return {
    totalDays,
    daysRemaining: Math.round(deadline.startOf("day").diff(now.startOf("day"), "days").days),
    deadline,
    // Worded as the engine words it, so the form and the container's own
    // drawer name the same rule.
    basis: `${totalDays} days`,
  };
}

/**
 * Whether the federal rule gives this generator status a central-accumulation
 * window at all.
 *
 * A VSQG has none, so no date the form is given will ever produce a
 * projection — which is a different thing to tell the reader than "you haven't
 * filled in the date yet".
 */
function hasCentralAccumulationLimit(
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined
): boolean {
  return generatorStatus != null && CENTRAL_ACCUMULATION_DAYS[generatorStatus] != null;
}

/**
 * What the form's clock card says when there is no projection to draw.
 *
 * Three different reasons produce no projection and they ask the reader for
 * different things: an unknown generator status is a gap in the facility's
 * data, a VSQG has no federal deadline to project at all, and a status that
 * does have a window is the only one of the three where filling in the
 * moved-to-central date will actually produce a dial.
 */
export function clockPlaceholderText(
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined,
  sqgOver200Miles: boolean
): string {
  const summary = generatorStatusSummary(generatorStatus, sqgOver200Miles);
  if (summary === null) {
    return "This facility's generator status isn't known, so no storage deadline can be projected.";
  }
  if (!hasCentralAccumulationLimit(generatorStatus)) {
    return `${summary}, so there's no deadline to project.`;
  }
  return "Set the moved-to-central date to project this container's deadline.";
}

/** "SQG — 180-day limit", the form's own label for the derived status. */
export function generatorStatusSummary(
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined,
  sqgOver200Miles: boolean
): string | null {
  if (!generatorStatus) {
    return null;
  }
  const abbreviation = GENERATOR_STATUS_ABBREVIATION[generatorStatus] ?? generatorStatus;
  const baseDays = CENTRAL_ACCUMULATION_DAYS[generatorStatus];
  if (baseDays == null) {
    return `${abbreviation} — no federal storage limit`;
  }
  const isSqg = generatorStatus === RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator;
  const days = isSqg && sqgOver200Miles ? SQG_OVER_200_MILES_DAYS : baseDays;
  return `${abbreviation} — ${days}-day limit`;
}

/**
 * The dates an episodic event bounds, checked here rather than left to the
 * pickers' min/max.
 *
 * A picker can stop you choosing an out-of-range date; it cannot notice that a
 * date you already chose went out of range when you ticked the attach box
 * afterwards. Save would then create the container and fail to attach it,
 * reporting a problem about a field the form still showed as fine.
 */
export function episodicDateIssues(
  dates: { accumulationStartDate: string | null; movedToCentralDate: string | null },
  episodicEvent: PreviewEpisodicEvent | undefined
): Issue[] {
  if (!episodicEvent) {
    return [];
  }
  const outOfWindow = (
    key: "accumulationStartDate" | "movedToCentralDate",
    date: string | null
  ): Issue | null => {
    if (
      !date ||
      !isOutsideEpisodicWindow(date, episodicEvent.startDate, episodicEvent.accumulationDeadline)
    ) {
      return null;
    }
    return {
      level: IssueLevel.Critical,
      key,
      message: `The episodic event runs ${formatContainerDate(episodicEvent.startDate)} – ${formatContainerDate(
        episodicEvent.accumulationDeadline
      )} — this date is outside it`,
      modelName: "WasteContainer",
      dataName: null,
      jurisdictions: [],
    };
  };
  return [
    outOfWindow("accumulationStartDate", dates.accumulationStartDate),
    outOfWindow("movedToCentralDate", dates.movedToCentralDate),
  ].filter((issue): issue is Issue => issue !== null);
}
